import { effect, inject, Injectable, signal, Injector } from '@angular/core';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import { RouterStore } from '@route/data-access';
import { selectFromMetadata } from '../entities/odata.model';
import { AuthStore } from '@auth/data-access';
import { ApiUrlsType, APP_CONFIG } from '@application/config';
import { EMPTY, filter, switchMap, take, timer } from 'rxjs';
import { HttpClient, HttpContext } from '@angular/common/http';
import { CustomStore, LoadOptions } from 'devextreme/common/data';
import { DX_PERMISSION_GUARD_CONFIG_TOKEN, DxPermissionGuardConfig } from '../entities/dx-permission-guard.model';
import { DateTime } from 'luxon';
import { normalizeId, StringHelper } from '@shared/utils';
import {
  adjustODataFilterDatesToUtc,
  isOffline,
  OFFLINE_CONFIRM_MS,
  SKIP_OFFLINE_RETRY,
  whenBackOnline,
} from '@application/http-client';
import { DateTimeService } from '@application/data-access';
import { installDxODataErrorBodySanitizer } from './dx-odata-error-body.sanitizer';

/** DevExtreme's own default for a `DataSource` built without one — `m_data_source.js:98`. */
const DEFAULT_SEARCH_OPERATION = 'contains';

export interface IDxLookupConfig {
  url: keyof ApiUrlsType | string;
  metadata: string;
  key?: string;
  keyType?: string;
  params?: object;
  selectedItem?: any;
  useOptionsMethod?: boolean;
  /**
   * Per-field Edm types, forwarded to the `ODataStore`. Force a Guid column to
   * `'String'` so reads hand back the plain uuid instead of a `Guid` object
   * (`{ _value }`), which is what the selected item carries into the caller.
   */
  fieldTypes?: Record<string, string>;
}

/**
 * Interface for visible column configuration
 */
export type TVisibleColumn = {
  dataField: string;
  dataType: 'string' | 'number' | 'date' | 'boolean' | 'object' | 'datetime';
};

export type TAggregate = string | { [field: string]: string } | string[];

type TRequest = {
  url: string;
  async: boolean;
  method: string;
  timeout: number;
  params: any;
  payload: any;
  headers: any;
  countFilter?: string;
};

@Injectable({ providedIn: 'root' })
export class DxOdataStoreService {
  private routeParams: any;
  private readonly injector = inject(Injector);
  private readonly routeStore = inject(RouterStore);
  private readonly http = inject(HttpClient);
  private readonly url = inject(APP_CONFIG).apiUrl;
  private readonly dateTimeService = inject(DateTimeService);

  /**
   * DataSources with a reload already queued for the next reconnection. One
   * failed load produces several errors for the same grid (rows, total summary,
   * group count), and every grid on screen fails at once during an outage — so
   * the schedule is per DataSource, and the reconnection replays one load each,
   * not one per error.
   */
  private readonly reloadOnReconnectScheduled = new WeakSet<object>();

  constructor() {
    // Blindagem global do parser de erros OData do DevExtreme. Idempotente, e
    // ancorada aqui porque este serviço é o único ponto do monorepo que
    // constrói um `ODataStore` — ver `dx-odata-error-body.sanitizer.ts`.
    installDxODataErrorBodySanitizer();

    effect(() => {
      this.routeParams = this.routeStore.getCurrentRouteParams();
    });
  }

  public fromOdata<T>({
    url,
    metadata,
    key,
    keyType,
    params,
    useOptionsMethod = false,
    mode = 'raw',
    permissionGuard,
    selectedItem,
    predefinedItems,
    exposeRequest = false,
    fieldTypes,
    groupby,
    canLoad,
    map,
  }: {
    url: keyof ApiUrlsType | string;
    metadata: string;
    map?: (row: any) => any;
    key?: string;
    keyType?: string;
    params?: any;
    useOptionsMethod?: boolean;
    mode?: 'raw' | 'lookup';
    permissionGuard?: DxPermissionGuardConfig;
    selectedItem?: any;
    predefinedItems?: any[];
    exposeRequest?: boolean;
    groupby?: string[];
    /**
     * Per-field Edm types. Use `'Guid'` for non-key UUID fields so runtime
     * `DataSource.filter([...])` calls serialize them as bare UUIDs (OData v4
     * Edm.Guid literal) instead of single-quoted strings — the backend rejects
     * `FieldId eq 'uuid'` with a type-mismatch ODataException.
     */
      fieldTypes?: Record<string, string>;
    /**
     * Guarda de consulta, lida a cada `load()`. Enquanto devolver `false` a
     * `DataSource` responde vazia sem tocar no servidor — é assim que um ecrã
     * que exige um filtro (uma aula, um protocolo) desenha a grade e a barra de
     * filtros sem disparar a consulta que varre o conjunto inteiro.
     *
     * Existe por causa do que a alternativa custa: trocar a `DataSource` por
     * `null` e de volta faz o DevExtreme carregar a nova assim que a ligação
     * muda, e a escolha do utilizador só lá chega 50ms depois, pelo
     * `applySelectedFilters`. Esse primeiro pedido sai exatamente sem o filtro
     * que se queria exigir. Mantendo a identidade da `DataSource` estável, o
     * `filter()` que se segue à escolha é que provoca a recarga — já com ela.
     *
     * Omitir mantém o comportamento de sempre: sem guarda, carrega sempre.
     */
    canLoad?: () => boolean;
  }): DataSource<T> {
    const request = signal<TRequest>(null);

    /**
     * Monotonic id of the current `load()`. A summary that resolves after a newer
     * load started describes a filter/page the grid no longer shows, so it is
     * dropped instead of published.
     */
    let loadSequence = 0;

    /**
     * Total summary that arrived *after* `load()` had already resolved with the
     * rows. `lib-dx-data-grid` reads this signal and repaints only the footer.
     * `null` means "nothing pending" — every `load()` resets it.
     */
    const deferredSummary = signal<(number | null)[] | null>(null);

    /**
     * Last summary resolved for the current query. DevExtreme caches `extra.summary`
     * per query and, once it has one, stops sending `totalSummary` on the following
     * page loads — so the store has to hand the same total back or the footer would
     * blank out on page 2. Cleared whenever a load does ask for a summary again,
     * which is exactly when the query changed.
     */
    let lastSummary: (number | null)[] | null = null;

    const odataDataSource: DataSource<T> = new DataSource<T>({
      ...(map ? { map } : {}),
      store: new ODataStore<T>({
        url: url + `/${metadata}`,
        key: key,
        version: 4,
        keyType: keyType ?? 'Guid',
        ...(fieldTypes ? { fieldTypes } : {}),
        beforeSend: (req) => {

        if (metadata === 'Exercises' && req?.params['$filter']) {
          req.params['$filter'] = StringHelper.normalizeODataFilter(req.params['$filter']);
        }

          if (useOptionsMethod) {
            req.method = 'OPTIONS';
          }

          if (req.url.includes('(')) {
            const url = req.url.split('(');
            req.url = url[0];
            req.params['$filter'] = `${key} eq ${url[1].replace(')', '')}`;
          }

          const authStore = this.injector.get(AuthStore);
          req.headers = {
            Authorization: 'Bearer ' + authStore.getAuthData()?.Token,
          };
          if (selectFromMetadata.has(metadata) && !req.params['$select']) {
            req.params['$select'] = selectFromMetadata.get(metadata);
          }

          const normalizeLookup = (): string => {
            if (req.params['$filter'].includes(key)) {
              return req.params['$filter']
            }
            return `(${req.params['$filter']}) and ${params['$filter']}`
          };


          if (params) {
            if (req.params['$filter'] && params['$filter']) {
              req.params = {
                ...req.params,
                ...params,
                ['$filter']: mode === 'lookup' ? normalizeLookup() : `(${req.params['$filter']}) and ${params['$filter']}`,
              };
            } else {
              req.params = { ...req.params, ...params };
            }

            if (params['$apply']) {
              req.params = { $apply: params['$apply'] };
            }
          }

          // OData rejects a standalone `$filter` next to `$apply=groupby(...)`, so
          // for grouped requests the filter is folded into the `$apply`. See
          // `applyGroupby` for how the `GroupedCount`-alias part is handled.
          let preGroupFilter: string | undefined;
          let countFilter: string | undefined;
          if (groupby?.length) {
            ({ preGroupFilter, countFilter } = this.applyGroupby(req, groupby));
          }

          // Snapshot pre-conversion params so HttpClient consumers (e.g. aggregate
          // calls in treasury.store.ts) receive the unconverted filter string —
          // DateInterceptor handles their single conversion pass. Only the
          // pre-group filter is re-exposed as `$filter` (the `GroupedCount` filter is
          // aggregate-only and travels separately for the group-count query).
          request.set({
            ...req,
            countFilter,
            params: {
              ...req.params,
              ...(preGroupFilter ? { ['$filter']: preGroupFilter } : {}),
            },
          });

          // O fuso vem do `DateTimeService`, não de `DateTime.now().zoneName`:
          // ver o porquê no cabeçalho de `adjustODataFilterDatesToUtc`.
          const userZone = this.dateTimeService.getUserZone();

          if (req.params?.$filter) {
            req.params.$filter = adjustODataFilterDatesToUtc(req.params.$filter, userZone);
          }

          if (req.params?.$apply) {
            req.params.$apply = adjustODataFilterDatesToUtc(req.params.$apply, userZone);
          }
        },
      }),
    });

    const customDataSource: DataSource<T> = new DataSource<T>({
      store: new CustomStore<T>({
        key,
        byKey: (keyValue: any, extraOptions?: LoadOptions<T>) => {
          const item = selectedItem || predefinedItems?.find((item) => normalizeId(item[key]) === normalizeId(keyValue));
          return item ? Promise.resolve([item]) : this.getData(odataDataSource, { filter: [key, '=', keyValue] });
        },
        load: async (options) => {
          // Filtro obrigatório ainda por preencher: a consulta iria varrer o
          // conjunto inteiro. Devolver vazio aqui é o que deixa a grade e a
          // barra de filtros no ecrã sem pedido nenhum ao servidor.
          if (canLoad && !canLoad()) {
            return { totalCount: 0, data: [], summary: [] };
          }

          // `getRowsToExport` loads straight from this store to build a report; it
          // never reads the summary (both export paths total the exported rows
          // locally) and must not disturb the summary the grid is showing.
          const isExportLoad = (options as { isLoadingAll?: boolean })?.isLoadingAll === true;
          // Absent `totalSummary` means DevExtreme is serving a cached total — a
          // page change on the same query, not a new one.
          const wantsSummary = !!options?.totalSummary && !isExportLoad;
          const loadId = wantsSummary ? ++loadSequence : loadSequence;

          if (wantsSummary) {
            deferredSummary.set(null);
            lastSummary = null;
          }

          // Deliberately not written back onto `options`. The grid adapter keeps
          // the very objects the grid handed us in `_lastLoadOptions` (shallow
          // `extend`, `m_data_source_adapter.js:276-283`) and compares the next
          // load's `sort` against them. Editing `selector` in place made that
          // comparison fail on every load of a compound `desc` sort, which flags
          // `sorting: true`, which throws away the adapter's cached total — the
          // same `$count`-on-every-page symptom `getData` already fixes once.
          const sort = this.normalizeSort(options?.sort);

          const { pageData: predefinedPageData, allData: predefinedAllData } = await this.getPredefinedItemsPromise(predefinedItems, { ...options, sort, requireTotalCount: true });
          const predefinedPageDataLength = predefinedPageData?.length ?? 0;
          const predefinedTotalCount = predefinedAllData?.length ?? 0;

          let take = options?.take;
          let skip = options?.skip;
          if (predefinedTotalCount > 0) {
            if (options?.take) {
              take = options.take - predefinedPageDataLength;
              if (take < 0) take = 0;
            }
            if (options?.skip) {
              skip = options.skip - predefinedTotalCount;
              if (skip < 0) skip = 0;
            }
          }

          // A `$apply=groupby(...)` response is a bare array with no
          // `@odata.count`, so letting the inner ODataStore add `$count=true`
          // makes DevExtreme throw E4018. Skip the store's count for grouped
          // queries and resolve the group count separately.
          const isGrouped = !!groupby?.length;

          const dataPromise = this.getData(odataDataSource, {
            ...options,
            sort,
            requireTotalCount: isGrouped ? false : options?.requireTotalCount,
            take,
            skip,
          });

          const summaryArgs = {
            url: request()?.url,
            filter: request()?.params?.['$filter'],
            notOdataParams: Object.fromEntries(Object.entries(request()?.params || {}).filter(([key]) => !key.startsWith('$')))
          };

          // The aggregate request scans the whole filtered set, so it is routinely
          // slower than the page of rows. It is kicked off together with the data
          // but deliberately *not* awaited: `settledSummary` stays undefined while
          // it is still in flight, and the value is published afterwards through
          // `deferredSummary`. The group count is different — it feeds `totalCount`
          // and the pager, so it still gates the result.
          // Loads that are not asking for a summary replay the last one instead, so
          // paging and exporting leave the footer as it is.
          let settledSummary: (number | null)[] | undefined = wantsSummary ? undefined : (lastSummary ?? []);
          const summaryPromise = wantsSummary
            ? (async () => {
                const summary = await this.getSummary(summaryArgs, options);
                const remoteCounts = predefinedTotalCount > 0
                  ? await this.getAverageNotNullCounts(summaryArgs, options)
                  : [];
                return this.joinSummary(predefinedAllData, summary, options, remoteCounts);
              })()
            : null;
          summaryPromise?.then(
            (summary) => { settledSummary = summary; if (loadId === loadSequence) lastSummary = summary; },
            () => { settledSummary = []; },
          );

          const groupCountPromise = isGrouped && options?.requireTotalCount
            ? this.getGroupCount(summaryArgs, groupby, request()?.countFilter)
            : Promise.resolve(null);

          return Promise.all([dataPromise, groupCountPromise]).then(([data, groupCount]) => {
            const dataFormatted = this.formatUtcDatesInData(data);
            const joinedData = Array.isArray(dataFormatted) ? (predefinedPageData || []).concat(dataFormatted) : dataFormatted;

            const remoteTotalCount = isGrouped
              ? (groupCount ?? (Array.isArray(dataFormatted) ? dataFormatted.length : 0))
              : odataDataSource.totalCount();

            // Still pending: hand the rows over now and let the footer catch up.
            // An empty summary (rather than the previous one) is intentional — a
            // stale total under a fresh filter reads as a wrong number.
            if (settledSummary === undefined) {
              summaryPromise?.then((summary) => {
                if (loadId === loadSequence) deferredSummary.set(summary);
              }, () => undefined);
            }

            return {
              totalCount: remoteTotalCount + predefinedTotalCount,
              data: joinedData,
              summary: settledSummary ?? []
            }
          }).catch((error) => {
            console.error(error);
            this.errorHandler(error, customDataSource);

            return {
                totalCount: 0,
                data: [],
                summary: []
              }
          });
        },
        errorHandler: (error) => this.errorHandler(error, customDataSource)
      }),
    })

    if (permissionGuard) {
      Object.defineProperty(customDataSource, DX_PERMISSION_GUARD_CONFIG_TOKEN, {
        value: permissionGuard,
        enumerable: false,
        configurable: true,
      });
    }

    Object.defineProperty(customDataSource, 'deferredSummary', {
      value: deferredSummary,
      enumerable: false,
      configurable: true,
    });

    if (exposeRequest) {
      Object.defineProperty(customDataSource, 'request', {
        value: request,
        enumerable: false,
        configurable: true,
      });
    }

    return customDataSource;
  };
  

  /**
   * Folds a grid filter into a grouped `$apply`. Plain row conditions run before
   * the `groupby`; conditions on the `GroupedCount` alias (which only exists after the
   * grouping) become a second `filter(...)` after it —
   * `filter(pre)/groupby(...)/filter(GroupedCount ...)`. Mutates `req.params` (drops the
   * standalone `$filter`, sets `$apply`) and returns both filter parts so the
   * caller can re-expose / forward them.
   */
  private applyGroupby(req: any, groupby: string[]): { preGroupFilter?: string; countFilter?: string } {
    const rawFilter: string | undefined = req.params?.$filter;
    if (rawFilter) delete req.params['$filter'];

    const { preGroupFilter, countFilter } = this.splitGroupFilter(rawFilter ?? '');
    const group = `groupby((${groupby.join(',')}),aggregate($count as GroupedCount))`;
    const pre = preGroupFilter ? `filter(${preGroupFilter})/` : '';
    const post = countFilter ? `/filter(${countFilter})` : '';
    req.params['$apply'] = `${pre}${group}${post}`;

    return { preGroupFilter, countFilter };
  }

  /**
   * Companion aggregate request of a grid load — the total summary, the group
   * count, the not-null counts. It goes through `HttpClient` (unlike the rows,
   * which DevExtreme's own ajax layer fetches), so it would otherwise be held
   * by `OfflineRetryInterceptor` during an outage.
   *
   * It opts out of that: the grid already owns its retry (see
   * `reloadOnReconnect`), and the reload re-issues this call along with the
   * rows. Being held here would mean paying twice for a scan of the whole
   * filtered set, and publishing a total for a query the grid has already
   * replaced.
   */
  private aggregateGet(url: string, params: Record<string, any>) {
    return this.http.get(url, {
      params,
      withCredentials: true,
      context: new HttpContext().set(SKIP_OFFLINE_RETRY, true),
    });
  }

  private async getSummary<T>({ url, filter, notOdataParams }, { totalSummary }: LoadOptions<T>): Promise<(number | null)[]> {
    if (!totalSummary) return [];
    const totalSummaryArray = Array.isArray(totalSummary) ? totalSummary : [totalSummary]
    const aggregates = totalSummaryArray
      .map(s => this.buildAggregateExpression(s['selector'], s['summaryType']))
      .filter((expression): expression is string => !!expression);
    if (!aggregates.length) return [];

    return await new Promise(resolve => {
      let $apply = '';
      if (filter) $apply += `filter(${filter})/`;
      $apply += `aggregate(${aggregates.join(', ')})`;

      this.aggregateGet(url, { ...notOdataParams, $apply }).subscribe({
        next: (response) => {
          if ("@odata.context" in response) response = response?.["value"];
          const summary = totalSummaryArray.map(s => response?.[0]?.[s['selector']] ?? null);
          resolve(summary);
        },
        error: () => resolve([])
      })
    })
  }

  /**
   * Counts the distinct groups produced by a `groupby(...)` transformation.
   * The inner ODataStore can't supply this (its `$count=true` triggers E4018 on
   * `$apply` responses), so we ask the backend directly:
   * `filter(...)/groupby((keys))/aggregate($count as Count)` collapses the group
   * rows into a single `{ Count }` row with the number of groups.
   */
  private async getGroupCount(
    { url, filter, notOdataParams },
    groupby: string[],
    countFilter?: string
  ): Promise<number | null> {
    if (!url || !groupby?.length) return 0;
    return await new Promise((resolve) => {
      let $apply = '';
      if (filter) $apply += `filter(${filter})/`;
      if (countFilter) {
        // Materialize the per-group `GroupedCount`, filter the groups by it,
        // then count what remains — so the total reflects the same
        // `GroupedCount` filter applied to the grouped data request.
        $apply += `groupby((${groupby.join(',')}),aggregate($count as GroupedCount))/filter(${countFilter})/aggregate($count as Count)`;
      } else {
        $apply += `groupby((${groupby.join(',')}))/aggregate($count as Count)`;
      }
      this.aggregateGet(url, { ...notOdataParams, $apply }).subscribe({
        next: (response) => {
          if ('@odata.context' in response) response = response?.['value'];
          resolve(response?.[0]?.['Count'] ?? 0);
        },
        error: () => resolve(null),
      });
    });
  }

  /**
   * Splits a raw OData filter into the part that must run **before** the
   * `groupby(...)` (plain row filters) and the part that references the
   * `GroupedCount` aggregate alias, which only exists **after** the grouping and
   * so must be applied as a second `filter(...)` further down the `$apply` chain.
   * A `GroupedCount ...` condition placed before `groupby` is rejected by OData
   * because the alias isn't in scope yet.
   */
  private splitGroupFilter(filter: string): { preGroupFilter?: string; countFilter?: string } {
    const preGroupConditions: string[] = [];
    const countConditions: string[] = [];
    for (const condition of this.splitTopLevelAnd(filter)) {
      if (/(^|[\s(])GroupedCount\b/.test(condition)) {
        countConditions.push(condition);
      } else {
        preGroupConditions.push(condition);
      }
    }
    return {
      preGroupFilter: preGroupConditions.length ? preGroupConditions.join(' and ') : undefined,
      countFilter: countConditions.length ? countConditions.join(' and ') : undefined,
    };
  }

  /**
   * Splits an OData filter on its top-level ` and ` operators, ignoring any
   * ` and ` nested inside parentheses. The outermost wrapping parentheses (if
   * the whole expression is enclosed) are stripped first so a fully wrapped
   * filter still splits into its individual conditions.
   */
  private splitTopLevelAnd(filter: string): string[] {
    const unwrapped = this.stripOuterParens(filter.trim());
    const quoted = this.quotedMask(unwrapped);
    const parts: string[] = [];
    let depth = 0;
    let start = 0;
    for (let i = 0; i < unwrapped.length; i++) {
      if (quoted[i]) continue;
      const char = unwrapped[i];
      if (char === '(') depth++;
      else if (char === ')') depth--;
      else if (depth === 0 && unwrapped.slice(i, i + 5).toLowerCase() === ' and ') {
        parts.push(unwrapped.slice(start, i));
        i += 4;
        start = i + 1;
      }
    }
    parts.push(unwrapped.slice(start));
    return parts.map((part) => part.trim()).filter(Boolean);
  }

  /**
   * Marks every character index that lies inside a single-quoted OData string
   * literal (the surrounding quotes included). A quote is escaped by doubling it
   * (`''`), which nets back to the same state, so an embedded `''` stays part of
   * the literal. Used by the structural scanners so parentheses and ` and `
   * operators that are really literal text (e.g. `Name eq 'a (b) and c'`) don't
   * throw off paren depth or trigger a split.
   */
  private quotedMask(filter: string): boolean[] {
    const mask = new Array<boolean>(filter.length).fill(false);
    let inQuotes = false;
    for (let i = 0; i < filter.length; i++) {
      if (filter[i] === "'") {
        mask[i] = true;
        inQuotes = !inQuotes;
      } else {
        mask[i] = inQuotes;
      }
    }
    return mask;
  }

  /**
   * Removes layers of parentheses that wrap the entire expression, repeating
   * while the outermost `(` matches the final `)`. Inner groups are left
   * untouched (e.g. `(A and B) and C` is returned unchanged).
   */
  private stripOuterParens(filter: string): string {
    let result = filter.trim();
    while (result.startsWith('(') && result.endsWith(')')) {
      const quoted = this.quotedMask(result);
      let depth = 0;
      let wrapsWhole = true;
      for (let i = 0; i < result.length; i++) {
        if (quoted[i]) continue;
        if (result[i] === '(') depth++;
        else if (result[i] === ')') {
          depth--;
          if (depth === 0 && i < result.length - 1) {
            wrapsWhole = false;
            break;
          }
        }
      }
      if (!wrapsWhole) break;
      result = result.slice(1, -1).trim();
    }
    return result;
  }

  /**
   * Maps a DevExtreme `summaryType` to its OData v4 `$apply/aggregate` expression,
   * aliased back to the selector so the response can be read by selector name.
   * Returns `null` for types that have no server-side aggregate (`custom`).
   */
  private buildAggregateExpression(selector: string, summaryType: string): string | null {
    switch (summaryType) {
      case 'sum':
      case 'min':
      case 'max':
        return `${selector} with ${summaryType} as ${selector}`;
      case 'avg':
        return `${selector} with average as ${selector}`;
      case 'count':
        return `$count as ${selector}`;
      default:
        return null;
    }
  }

  private async getData<T>(dataSource: DataSource<T, any>, options: LoadOptions<T>) {
    const {
      filter,
      requireTotalCount,
      searchExpr,
      searchOperation,
      searchValue,
      select,
      skip,
      sort,
      take,
      group,
      totalSummary,
      groupSummary,
      userData,
      parentIds,
      requireGroupCount,
      customQueryParams,
      startDate,
      endDate,
      expand,
    } = options;
    
    
    dataSource.group(group || null);
    dataSource.select(select || null);
    dataSource.sort(sort || null);
    dataSource.searchValue(searchValue || null);
    // Three of the accessors below guard on the value's *type* instead of on
    // `arguments.length`, so handing them `null` reads as a getter and writes
    // nothing: `requireTotalCount` wants a boolean (`m_data_source.js:211-216`),
    // `searchOperation` a string (`:224-229`), `pageSize` a number (`:167-172`).
    // The others (`group`, `select`, `sort`, `searchValue`, `searchExpr`,
    // `filter`) do take `null` and clear properly.
    //
    // This inner `DataSource` is reused across every load, so a write that is
    // silently skipped leaves the previous load's value latched. That is what
    // made `$count=true` ride along on every page: DevExtreme stops sending
    // `requireTotalCount` once its adapter has a cached total, `|| null` failed
    // to clear the `true` the first page had written, and the flag stuck.
    dataSource.requireTotalCount(!!requireTotalCount);
    dataSource.searchExpr(searchExpr || null);
    // `'contains'` is what a fresh `DataSource` starts with (`m_data_source.js:98`),
    // so writing it back is what "no operation asked for" has to mean here.
    dataSource.searchOperation(
      typeof searchOperation === 'string' ? searchOperation : DEFAULT_SEARCH_OPERATION
    );
    dataSource.filter(filter || null);
    dataSource.paginate(!!take);
    // `0` rather than `null` so the write lands. `_createStoreLoadOptions` reads
    // it as falsy and emits no `$skip`/`$top` (`m_data_source.js:435-441`), which
    // is the same wire result — but the internal state now matches the request
    // instead of keeping a stale page size behind the `paginate(false)` mask.
    dataSource.pageSize(take || 0);
    dataSource.pageIndex((skip || 0) / (take || 1));
    return await dataSource.load();
  }

  /**
   * Queues a reload of the grid for when the connection comes back.
   *
   * Grids do not go through `HttpClient`: DevExtreme's `ODataStore` uses its own
   * ajax layer, so `OfflineRetryInterceptor` never sees their requests and they
   * cannot be replayed the same way — the failed `load()` has already resolved
   * with an empty page by the time we get here. The equivalent at this layer is
   * to load again, which is what `reload()` does: it re-issues the current
   * query and repaints the grid.
   *
   * Two limits worth knowing:
   * - The reload is bound to the DataSource, not to the screen. A grid the user
   *   left during the outage (its widget destroyed, but the DataSource kept
   *   alive by the store that created it) still reloads once, into nothing.
   *   That costs one request, capped by the wait window, and is the price of
   *   there being no reliable "is this grid still on screen" signal here.
   * - `_disposed` is a private DevExtreme flag, used because the class exposes
   *   no public equivalent — `dispose()` deletes `_store`, so reloading a
   *   disposed DataSource throws. The `catch` is the backstop if the flag ever
   *   moves.
   */
  private reloadOnReconnect<T>(customDataSource: DataSource<T, any>): void {
    if (this.reloadOnReconnectScheduled.has(customDataSource)) return;
    this.reloadOnReconnectScheduled.add(customDataSource);

    const release = () => this.reloadOnReconnectScheduled.delete(customDataSource);

    // `navigator.onLine` lags the failure that it explains, so the check waits a
    // beat — see `OFFLINE_CONFIRM_MS`. Unlike the interceptor, a failure that
    // never turns into a confirmed outage gets no blind attempt here: a reload
    // that fails lands back in this same handler, and retrying on an
    // unexplained error would be a loop with nothing to break it.
    timer(OFFLINE_CONFIRM_MS)
      .pipe(switchMap(() => (isOffline() ? whenBackOnline() : EMPTY)))
      .subscribe({
        next: () => {
          release();
          if ((customDataSource as unknown as { _disposed?: boolean })._disposed) return;
          try {
            customDataSource.reload();
          } catch (error) {
            console.error('[dx-odata] reload on reconnect failed', error);
          }
        },
        // Outage longer than the wait window: the user is no longer waiting for
        // this grid, so it stays as it is until something asks for it again.
        error: release,
        // Never confirmed as an outage: nothing to reload, so free the slot for
        // a later failure of this grid.
        complete: release,
      });
  }

  private errorHandler<T>(error, customDataSource: DataSource<T, any>) {
    // No response reached the browser: nothing to report and nothing the user
    // can act on — if it turns out to be an outage, the grid just has to be
    // loaded again once there is a network. Whether it *is* one is decided in
    // `reloadOnReconnect`, which waits for the browser to make up its mind
    // before reading the flag.
    if (error?.httpStatus === 0) {
      this.reloadOnReconnect(customDataSource);
      return;
    }

    if (error.httpStatus === 401) {
      const authStore = this.injector.get(AuthStore);
      const currentToken = authStore.getAuthData()?.Token;
      authStore
        .observableAuthData()
        .pipe(
          filter(
            (authData) =>
              !!authData?.Token && authData.Token !== currentToken
          ),
          take(1)
        )
        .subscribe(() => {
          customDataSource.reload();
        });
      authStore.refreshToken();
    }
  }


  /**
   * Normaliza, em profundidade, as datas que o `ODataStore` já converteu em
   * `Date`. As respostas destas grades não passam pelo `DateInterceptor` — o
   * `ODataStore` tem a sua própria camada de ajax, fora do `HttpClient` — então
   * é aqui que a conversão mora.
   *
   * ## O que o DevExtreme faz com a string, ao certo
   *
   * O `parseISO8601` dele (`devextreme/.../data/odata/m_utils.js`) **descarta o
   * `Z`** e escreve os dígitos com setters LOCAIS (`setFullYear`, `setHours`,
   * ...). Não é uma leitura em UTC: o resultado é um `Date` cujo relógio de
   * parede NA ZONA DA MÁQUINA são os dígitos crus que o servidor mandou.
   *
   * Isto é o oposto do que se assume à primeira vista, e é a peça que explica os
   * dois ramos — ou melhor, a falta de um deles.
   *
   * ## Por que as colunas SEM "Utc" no nome não são tocadas
   *
   * `DueOn`, `AddedOn`, ... são hora de parede: os dígitos crus já são o valor a
   * mostrar. E o `getDateTimeFromJSDate`, por baixo de todos os pipes, lê o
   * `Date` outra vez na zona da MÁQUINA — a mesma em que o DevExtreme os
   * escreveu. As duas dependências da máquina cancelam-se e o que sai é
   * exatamente o dígito do servidor, seja qual for o fuso do SO.
   *
   * Não acrescentar aqui um ramo para elas. Já houve um, que relia o valor com
   * `{ zone: SOZone }` e o reancorava no fuso da conta: com máquina e conta no
   * mesmo fuso era inócuo, e com fusos diferentes introduzia o desvio que não
   * existia (`DueOn` de 01/04 a sair 31/03 numa máquina em Los Angeles).
   *
   * ## Por que as colunas `*Utc` são
   *
   * Aí os dígitos crus são um instante UTC de verdade, e o que se quer mostrar é
   * a hora de parede da CONTA. A cadeia abaixo lê o `Date` em UTC, passa o
   * instante para o fuso da conta e reancora esses dígitos como UTC — de modo
   * que a releitura na zona da máquina, lá à frente, devolva a hora da conta.
   * Também aqui as duas dependências da máquina se cancelam.
   */
  private formatUtcDatesInData<T>(data: T): T {
    if (!data) {
      return data;
    }

    if (Array.isArray(data)) {
      return data.map(item => this.formatUtcDatesInData(item)) as T;
    }

    if (typeof data !== 'object' || data instanceof Date) {
      return data;
    }

    const result = { ...data } as T;

    for (const [key, value] of Object.entries(data)) {
      if (value instanceof Date) {
        if (key.toLowerCase().includes('utc')) {
          const userZone = this.dateTimeService.getUserZone();
          const utcDate = DateTime.fromJSDate(value, { zone: 'utc' })
            .setZone(userZone, { keepLocalTime: false }).setZone('utc', { keepLocalTime: true });

          (result as any)[key] = utcDate.toJSDate();
        }
      } 
    }

    return result;
  }

  /**
   * OData wants `desc` repeated on every segment of a compound selector except
   * the last one, which already carries it through the `desc` flag.
   *
   * Returns a new value and never touches the one it was given — see the note at
   * the call site for what editing the grid's own sort objects in place broke.
   * An item that needs no rewriting is handed back as-is, so the common
   * single-field case allocates nothing.
   */
  private normalizeSort(sort: any): any {
    if (Array.isArray(sort)) return sort.map((item) => this.normalizeSortItem(item));
    return this.normalizeSortItem(sort);
  }

  private normalizeSortItem(sort: any): any {
    if (typeof sort?.selector !== 'string' || !sort?.desc) return sort;
    // Nothing to spell out on a single field — `desc` already says it.
    if (!sort.selector.includes(',')) return sort;

    const selectors = sort.selector.split(',');
    return {
      ...sort,
      selector: selectors
        .map((selector: string, index: number) =>
          index !== selectors.length - 1 ? `${selector} desc` : selector
        )
        .join(','),
    };
  } 

  private async getPredefinedItemsPromise(items: any[], options?: any): Promise<{
    pageData: any[];
    allData: any[];
  }>{
    const isArray = Array.isArray(items);
    if (!isArray || (isArray && !items?.length)) return { pageData: [], allData: [] };

    const predefinedItems = new DataSource(items);

    return {
      pageData: await this.getData(predefinedItems, options),
      allData:  await this.getData(predefinedItems, { ...options, take: null, skip: null }),
    };
  }

  /**
   * Combines the remote summary (computed on the backend via `$apply/aggregate`)
   * with the contribution of the predefined items pinned to the top of the grid.
   *
   * `summary` arrives as one number per `totalSummary` entry, in the same order.
   * For each aggregate we recompute the total according to `summaryType`:
   * - `sum`     → remote sum + local sum
   * - `count`   → remote count + number of predefined items
   * - `min`     → smaller of remote and local (remote ignored when null/absent)
   * - `max`     → greater of remote and local (remote ignored when null/absent)
   * - `avg`     → weighted average using `remoteCounts[index]` (the remote count
   *              of non-null rows for that selector, fetched by
   *              `getAverageNotNullCounts`) as the weight of the remote value
   */
  private joinSummary(items: any[], summary: (number | null)[], options: any, remoteCounts: number[] = []): (number | null)[] {
    const { totalSummary } = options ?? {};
    if (!totalSummary || !items?.length) return summary ?? [];

    const totalSummaryArray = Array.isArray(totalSummary) ? totalSummary : [totalSummary];

    return totalSummaryArray.map((aggregate, index) => {
      // Keep `remoteValue` potentially null/undefined: when there are no remote
      // records (or every value of the selector is null) the backend min/max
      // comes back null. Defaulting it to 0 would corrupt min/max (e.g. local
      // [10, 20] would yield Math.min(0, 10, 20) = 0 instead of 10).
      const remoteValue = summary?.[index];
      const selector = aggregate['selector'];
      const summaryType = aggregate['summaryType'];

      // Exclude null/undefined/'' before Number() so the local count matches the
      // remote count semantics (`<selector> ne null`). Without this, Number(null)
      // becomes 0, passes !isNaN, and inflates the local weight/min/max.
      const localValues = items
        .map((item) => item?.[selector])
        .filter((value) => value !== null && value !== undefined && value !== '')
        .map(Number)
        .filter((value) => !isNaN(value));

      switch (summaryType) {
        case 'sum':
          return (remoteValue ?? 0) + localValues.reduce((acc, value) => acc + value, 0);
        case 'count':
          return (remoteValue ?? 0) + items.length;
        case 'min': {
          const values = remoteValue != null ? [remoteValue, ...localValues] : localValues;
          return values.length ? Math.min(...values) : null;
        }
        case 'max': {
          const values = remoteValue != null ? [remoteValue, ...localValues] : localValues;
          return values.length ? Math.max(...values) : null;
        }
        case 'avg': {
          const remoteCount = remoteCounts?.[index] ?? 0;
          const localSum = localValues.reduce((acc, value) => acc + value, 0);
          const totalCount = remoteCount + localValues.length;
          return totalCount ? ((remoteValue ?? 0) * remoteCount + localSum) / totalCount : (remoteValue ?? null);
        }
        default:
          return remoteValue ?? null;
      }
    });
  }

  /**
   * Fetches, per `avg` selector, the remote count of rows where the selector
   * is not null — the correct weight for a weighted average. Non-avg entries
   * resolve to `0`. One request per `avg` selector; meant to be called only
   * when there are predefined items to combine, so the extra round-trips are
   * skipped on the common path.
   */
  private async getAverageNotNullCounts<T>(
    { url, filter, notOdataParams },
    { totalSummary }: LoadOptions<T>
  ): Promise<number[]> {
    if (!totalSummary || !url) return [];
    const totalSummaryArray = Array.isArray(totalSummary) ? totalSummary : [totalSummary];
    return Promise.all(
      totalSummaryArray.map((s) =>
        s['summaryType'] === 'avg'
          ? this.getNotNullCount({ url, filter, notOdataParams }, s['selector'])
          : Promise.resolve(0)
      )
    );
  }

  private async getNotNullCount({ url, filter, notOdataParams }, selector: string): Promise<number> {
    if (!url) return 0;
    return await new Promise((resolve) => {
      const notNullFilter = filter ? `(${filter}) and ${selector} ne null` : `${selector} ne null`;
      const $apply = `filter(${notNullFilter})/aggregate($count as Count)`;
      this.aggregateGet(url, { ...notOdataParams, $apply }).subscribe({
        next: (response) => {
          if ('@odata.context' in response) response = response?.['value'];
          resolve(response?.[0]?.['Count'] ?? 0);
        },
        error: () => resolve(0),
      });
    });
  }
}
